import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ArrowLeft, BookOpen, Volume2, VolumeX, Play, Pause, Loader2,
  Sparkles, Heart, BookMarked, ChevronRight, RefreshCw
} from 'lucide-react';
import VoiceSelector, { VOICE_OPTIONS } from '@/components/VoiceSelector';

const BibleChapterReading = () => {
  const { chapterId } = useParams();
  const [searchParams] = useSearchParams();
  const childId = searchParams.get('child');
  const { api } = useAuth();
  
  const [chapter, setChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('kid_friendly');
  const [generatingKidFriendly, setGeneratingKidFriendly] = useState(false);
  const [isReading, setIsReading] = useState(false);
  
  // TTS Settings
  const [ttsVoice, setTtsVoice] = useState('shimmer');
  const [ttsSpeed, setTtsSpeed] = useState(0.75);
  const audioRef = useRef(null);

  useEffect(() => {
    if (chapterId) {
      fetchChapter();
    }
  }, [chapterId]);

  const fetchChapter = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/bible/chapters/${chapterId}?version=both`);
      setChapter(response.data);
    } catch (error) {
      console.error('Failed to fetch chapter:', error);
      toast.error('Failed to load chapter');
    } finally {
      setLoading(false);
    }
  };

  const generateKidFriendly = async () => {
    setGeneratingKidFriendly(true);
    try {
      const response = await api.post(`/bible/chapters/${chapterId}/kid-friendly`);
      setChapter(prev => ({
        ...prev,
        kid_friendly_text: response.data.kid_friendly_text
      }));
      toast.success('Kid-friendly version generated!');
    } catch (error) {
      console.error('Failed to generate kid-friendly:', error);
      toast.error('Failed to generate kid-friendly version');
    } finally {
      setGeneratingKidFriendly(false);
    }
  };

  const speakText = async (text) => {
    if (isReading) {
      // Stop reading
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      setIsReading(false);
      return;
    }

    try {
      setIsReading(true);
      const response = await api.post(
        `/voice/speak?text=${encodeURIComponent(text.substring(0, 4000))}&voice=${ttsVoice}&speed=${ttsSpeed}`,
        {},
        { responseType: 'blob' }
      );
      
      const audioUrl = URL.createObjectURL(response.data);
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.onended = () => setIsReading(false);
        audioRef.current.onerror = () => setIsReading(false);
        await audioRef.current.play();
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsReading(false);
      toast.error('Could not generate audio. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!chapter) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Chapter Not Found</h2>
          <Link to={`/bible-stories/${childId}`}>
            <Button>Back to Bible Stories</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50">
      <audio ref={audioRef} />
      
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md p-4 sticky top-0 z-50 shadow-sm">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <Link to={`/bible-stories/${childId}`}>
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="text-center">
              <h1 className="font-bold text-purple-800">{chapter.title}</h1>
              <p className="text-sm text-purple-600">{chapter.book} {chapter.chapter}</p>
            </div>
            <div className="w-10" />
          </div>
          
          {/* Voice Settings */}
          <div className="mt-3">
            <VoiceSelector
              voice={ttsVoice}
              onVoiceChange={setTtsVoice}
              speed={ttsSpeed}
              onSpeedChange={setTtsSpeed}
              theme="light"
              className="bg-purple-50 border-purple-200"
            />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Theme & Memory Verse */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card className="bg-gradient-to-r from-purple-100 to-pink-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-purple-800 mb-1">Theme: {chapter.theme}</h3>
                  <p className="text-sm text-purple-700 italic">"{chapter.memory_verse}"</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Reading Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="kid_friendly" className="data-[state=active]:bg-pink-100 data-[state=active]:text-pink-700">
              <Sparkles className="w-4 h-4 mr-2" />
              Kid-Friendly
            </TabsTrigger>
            <TabsTrigger value="original" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
              <BookMarked className="w-4 h-4 mr-2" />
              Original Text
            </TabsTrigger>
          </TabsList>

          <TabsContent value="kid_friendly">
            <Card>
              <CardContent className="p-6">
                {chapter.kid_friendly_text ? (
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="font-bold text-lg text-pink-700 flex items-center gap-2">
                        <Sparkles className="w-5 h-5" />
                        Story Time!
                      </h2>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => speakText(chapter.kid_friendly_text)}
                        className="border-pink-300 text-pink-600 hover:bg-pink-50"
                      >
                        {isReading ? (
                          <>
                            <VolumeX className="w-4 h-4 mr-2" />
                            Stop
                          </>
                        ) : (
                          <>
                            <Volume2 className="w-4 h-4 mr-2" />
                            Read Aloud
                          </>
                        )}
                      </Button>
                    </div>
                    <div className="prose prose-pink max-w-none">
                      {chapter.kid_friendly_text.split('\n\n').map((paragraph, idx) => (
                        <p key={idx} className="text-slate-700 leading-relaxed mb-4 text-lg">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Sparkles className="w-16 h-16 text-pink-300 mx-auto mb-4" />
                    <h3 className="font-bold text-slate-800 mb-2">Generate Kid-Friendly Version</h3>
                    <p className="text-slate-600 mb-4">
                      Our AI will rewrite this chapter in simple, engaging language perfect for children!
                    </p>
                    <Button
                      onClick={generateKidFriendly}
                      disabled={generatingKidFriendly}
                      className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
                    >
                      {generatingKidFriendly ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating Story...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Create Story Version
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="original">
            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-bold text-lg text-purple-700 flex items-center gap-2">
                    <BookMarked className="w-5 h-5" />
                    Scripture Reading
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => speakText(chapter.original_text)}
                    className="border-purple-300 text-purple-600 hover:bg-purple-50"
                  >
                    {isReading ? (
                      <>
                        <VolumeX className="w-4 h-4 mr-2" />
                        Stop
                      </>
                    ) : (
                      <>
                        <Volume2 className="w-4 h-4 mr-2" />
                        Read Aloud
                      </>
                    )}
                  </Button>
                </div>
                <div className="prose prose-purple max-w-none">
                  {chapter.original_text?.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="text-slate-700 leading-relaxed mb-4">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Navigation */}
        <div className="flex justify-center">
          <Link to={`/bible-stories/${childId}`}>
            <Button variant="outline" className="border-purple-300 text-purple-600">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to All Stories
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default BibleChapterReading;
